/**
 * Rendert eine Tabellenzelle entweder im Ansichts- oder Bearbeitungsmodus
 */
import React, { useState, useEffect, useRef } from "react";
import {
  Autocomplete,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Box,
  InputAdornment,
  Checkbox,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { parse, format, isValid } from "date-fns";
import { NumericFormat } from "react-number-format";
import { EditableFieldConfig } from "./types";
import { colors } from "../../../styles/colors";
import { borderRadius } from "../../../styles/theme";

interface EditableCellProps {
  /** Aktueller Wert */
  value: any;
  /** Feldkonfiguration */
  fieldConfig: EditableFieldConfig;
  /** Ist die Zelle im Bearbeitungsmodus? */
  isEditing: boolean;
  /** Fehlermeldung (falls vorhanden) */
  error?: string;
  /** Callback bei Wertänderung */
  onChange: (value: any) => void;
  /** Callback beim Verlassen des Feldes */
  onBlur: () => void;
  /** Callback beim Drücken der Enter-Taste (Speichern) - übergibt den aktuellen Wert direkt */
  onEnterPress?: (currentValue: any) => void;
  /** Optional: Custom-Render für Anzeigemodus */
  renderDisplay?: (value: any) => React.ReactNode;
}

/**
 * Kompakte Styles für Inline-Editing in Tabellenzellen
 */
const compactInputStyles = {
  "& .MuiOutlinedInput-root": {
    // Muss borderRadius.field sein, weil der Rahmen (notchedOutline) diesen Radius aus
    // dem Theme bekommt. Ein kleinerer Radius hier lässt die weiße Feldfüllung an den
    // Ecken über die Rundung des Rahmens hinausstehen.
    borderRadius: `${borderRadius.field}px`,
    backgroundColor: "white",
    boxShadow: `0 1px 3px ${colors.shadow.medium}`,
    minHeight: "36px",
    "& fieldset": {
      borderColor: colors.border.medium,
    },
    "&:hover fieldset": {
      borderColor: colors.border.focus,
    },
    "&.Mui-focused fieldset": {
      borderColor: colors.border.focus,
      borderWidth: 2,
    },
    "&.Mui-error fieldset": {
      borderColor: colors.border.error,
    },
  },
  "& .MuiOutlinedInput-input": {
    padding: "8px 12px",
    fontSize: "0.875rem",
  },
};

const checkboxStyles = {
  color: colors.border.medium,
  "&.Mui-checked": { color: colors.brand.primary },
};

const compactSelectStyles = {
  // s. compactInputStyles
  borderRadius: `${borderRadius.field}px`,
  backgroundColor: "white",
  boxShadow: `0 1px 3px ${colors.shadow.medium}`,
  minHeight: "36px",
  fontSize: "0.875rem",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: colors.border.medium,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: colors.border.focus,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: colors.border.focus,
    borderWidth: 2,
  },
  "& .MuiSelect-select": {
    padding: "8px 12px",
  },
};

/**
 * NumericFormat Custom Input für MUI TextField
 */
interface NumericFormatCustomProps {
  inputRef: (instance: HTMLInputElement | null) => void;
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
  allowDecimals?: boolean;
  allowNegative?: boolean;
}

const NumericFormatCustom = React.forwardRef<any, NumericFormatCustomProps>(
  function NumericFormatCustom(props, ref) {
    const {
      inputRef,
      onChange,
      allowDecimals = true,
      allowNegative = false,
      ...other
    } = props;

    return (
      <NumericFormat
        {...other}
        getInputRef={inputRef}
        thousandSeparator="."
        decimalSeparator=","
        allowNegative={allowNegative}
        decimalScale={allowDecimals ? undefined : 0}
        onValueChange={(values) => {
          onChange({
            target: {
              name: props.name,
              value: values.value,
            },
          });
        }}
      />
    );
  },
);

/** Spezieller Wert für die "Individuell eingeben..." Option */
const CUSTOM_VALUE_MARKER = "__custom__";

const EditableCell: React.FC<EditableCellProps> = ({
  value,
  fieldConfig,
  isEditing,
  error,
  onChange,
  onBlur,
  onEnterPress,
  renderDisplay,
}) => {
  // Lokaler State für den Eingabewert
  const [localValue, setLocalValue] = useState<string>(value?.toString() ?? "");
  const inputRef = useRef<HTMLInputElement>(null);

  // State für Custom-Value-Modus bei Dropdowns mit allowCustomValue
  const [isCustomMode, setIsCustomMode] = useState<boolean>(false);

  // Prüfe ob der aktuelle Wert in den Dropdown-Optionen vorhanden ist
  const isValueInOptions =
    fieldConfig.type === "dropdown" && fieldConfig.options
      ? fieldConfig.options.some(
          (opt) =>
            opt.value === value || opt.value?.toString() === value?.toString(),
        )
      : true;

  // Synchronisiere lokalen State mit externem Wert
  useEffect(() => {
    setLocalValue(value?.toString() ?? "");

    // Bei Dropdowns mit allowCustomValue: Nur initial den Custom-Mode setzen,
    // nicht zurücksetzen wenn der User gerade manuell im Custom-Mode tippt
    if (
      fieldConfig.type === "dropdown" &&
      fieldConfig.allowCustomValue &&
      fieldConfig.options &&
      !isCustomMode
    ) {
      const valueInOptions = fieldConfig.options.some(
        (opt) =>
          opt.value === value || opt.value?.toString() === value?.toString(),
      );
      if (
        value !== undefined &&
        value !== null &&
        value !== "" &&
        !valueInOptions
      ) {
        setIsCustomMode(true);
      }
    }
  }, [value, fieldConfig]); // eslint-disable-line react-hooks/exhaustive-deps -- isCustomMode bewusst ausgelassen

  // Fokus setzen wenn Editing startet
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  // Anzeigemodus
  if (!isEditing) {
    if (renderDisplay) {
      return <>{renderDisplay(value)}</>;
    }

    // Standard-Anzeige basierend auf Feldtyp
    let displayValue = value;

    if (fieldConfig.type === "checkbox") {
      const checked = value === true || value === "true";
      return (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Checkbox
            checked={checked}
            disabled
            size="small"
            sx={{
              ...checkboxStyles,
              "&.Mui-disabled": {
                color: checked ? colors.brand.primary : colors.border.medium,
              },
            }}
          />
        </Box>
      );
    }

    if (fieldConfig.type === "date" && value) {
      const parts = String(value).split("-");
      if (parts.length === 3) {
        displayValue = `${parts[2]}.${parts[1]}.${parts[0].slice(2)}`;
      }
    } else if (fieldConfig.type === "dropdown" && fieldConfig.options) {
      const option = fieldConfig.options.find((opt) => opt.value === value);
      displayValue = option?.label ?? value;
    } else if (
      fieldConfig.type === "number" &&
      value !== undefined &&
      value !== null
    ) {
      // Formatiere Zahl mit deutschen Separatoren
      const num = typeof value === "string" ? parseFloat(value) : value;
      if (!isNaN(num)) {
        displayValue = num.toLocaleString("de-DE", {
          minimumFractionDigits:
            fieldConfig.allowDecimals === false ? 0 : undefined,
          maximumFractionDigits: fieldConfig.allowDecimals === false ? 0 : 2,
        });
        if (fieldConfig.suffix) {
          displayValue = `${displayValue} ${fieldConfig.suffix}`;
        }
      }
    }

    return (
      <Typography
        variant="body2"
        sx={{
          fontWeight: "medium",
        }}
      >
        {displayValue}
      </Typography>
    );
  }

  // Bearbeitungsmodus
  const handleChange = (newValue: string) => {
    setLocalValue(newValue);

    // Bei Dropdowns sofort onChange aufrufen
    if (fieldConfig.type === "dropdown") {
      onChange(newValue);
    }
  };

  const handleBlur = () => {
    // Wert an Parent übergeben
    if (fieldConfig.type === "number") {
      // Leere Eingabe oder nur Whitespace -> undefined
      const trimmed = localValue.trim();
      if (trimmed === "") {
        onChange(undefined);
      } else {
        // Parse und validiere Zahl
        const num = parseFloat(trimmed.replace(",", "."));
        onChange(isNaN(num) ? undefined : num);
      }
    } else if (fieldConfig.type === "date") {
      onChange(localValue || undefined);
    } else {
      onChange(localValue);
    }
    onBlur();
  };

  /**
   * Handler für Enter-Taste: Speichert die Eingabe
   * Übergibt den aktuellen Wert direkt, um Race-Conditions zu vermeiden
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && onEnterPress) {
      e.preventDefault();

      // Parse den aktuellen Wert
      let parsedValue: any;
      if (fieldConfig.type === "number") {
        const trimmed = localValue.trim();
        if (trimmed === "") {
          parsedValue = undefined;
        } else {
          const num = parseFloat(trimmed.replace(",", "."));
          parsedValue = isNaN(num) ? undefined : num;
        }
      } else if (fieldConfig.type === "date") {
        parsedValue = localValue || undefined;
      } else {
        parsedValue = localValue;
      }

      // Wert auch im State aktualisieren (für Konsistenz)
      onChange(parsedValue);

      // Wert direkt übergeben - kein setTimeout mehr nötig!
      onEnterPress(parsedValue);
    }
  };

  // Checkbox-Feld
  if (fieldConfig.type === "checkbox") {
    const checked = value === true || value === "true";
    return (
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Checkbox
          checked={checked}
          onChange={(e) => {
            onChange(e.target.checked);
            onBlur();
          }}
          size="small"
          sx={checkboxStyles}
        />
      </Box>
    );
  }

  // Datum-Feld (MUI DatePicker)
  if (fieldConfig.type === "date") {
    const dateValue = localValue
      ? (() => {
          const d = parse(localValue, "yyyy-MM-dd", new Date());
          return isValid(d) ? d : null;
        })()
      : null;

    return (
      <Box sx={{ minWidth: 130 }}>
        <DatePicker
          value={dateValue}
          onChange={(newValue: Date | null) => {
            if (newValue && isValid(newValue)) {
              const iso = format(newValue, "yyyy-MM-dd");
              setLocalValue(iso);
              onChange(iso);
            } else {
              setLocalValue("");
              onChange(undefined);
            }
          }}
          format="dd.MM.yy"
          slotProps={{
            textField: {
              fullWidth: true,
              size: "small",
              error: !!error,
              onBlur,
              onKeyDown: handleKeyDown,
              inputRef,
              sx: {
                ...compactInputStyles,
                "& .MuiInputBase-input::placeholder": {
                  fontSize: "0.875rem",
                },
              },
            },
            openPickerButton: {
              sx: { "& .MuiSvgIcon-root": { fontSize: 18 } },
            },
          }}
        />
        {error && (
          <Typography
            variant="caption"
            color="error"
            sx={{ mt: 0.5, display: "block" }}
          >
            {error}
          </Typography>
        )}
      </Box>
    );
  }

  // Dropdown-Feld (mit optionalem Custom-Value-Modus)
  if (fieldConfig.type === "dropdown") {
    // Custom-Value-Modus: Zeige Eingabefeld statt Dropdown
    if (fieldConfig.allowCustomValue && isCustomMode) {
      const isTextMode = fieldConfig.customValueType === "text";

      const handleCustomBlur = () => {
        const trimmed = localValue.trim();
        if (trimmed === "") {
          onChange(undefined);
        } else if (isTextMode) {
          onChange(trimmed);
        } else {
          const num = parseFloat(trimmed.replace(",", "."));
          onChange(isNaN(num) ? undefined : num);
        }
        onBlur();
      };

      const handleCustomKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Escape") {
          setIsCustomMode(false);
          setLocalValue("");
        } else if (e.key === "Enter" && onEnterPress) {
          e.preventDefault();
          const trimmed = localValue.trim();
          let parsedValue: any;
          if (isTextMode) {
            parsedValue = trimmed || undefined;
          } else {
            parsedValue =
              trimmed === ""
                ? undefined
                : parseFloat(trimmed.replace(",", "."));
            if (typeof parsedValue === "number" && isNaN(parsedValue))
              parsedValue = undefined;
          }
          onChange(parsedValue);
          onEnterPress(parsedValue);
        }
      };

      return (
        <Box sx={{ minWidth: 100 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={handleCustomBlur}
            onKeyDown={handleCustomKeyDown}
            error={!!error}
            inputRef={inputRef}
            placeholder={isTextMode ? "Typ eingeben..." : "Wert eingeben..."}
            {...(!isTextMode && {
              InputProps: {
                inputComponent: NumericFormatCustom as any,
              },
              inputProps: {
                allowDecimals: fieldConfig.allowDecimals ?? false,
                allowNegative: fieldConfig.allowNegative ?? false,
              },
            })}
            sx={compactInputStyles}
          />
          {error && (
            <Typography
              variant="caption"
              color="error"
              sx={{ mt: 0.5, display: "block" }}
            >
              {error}
            </Typography>
          )}
        </Box>
      );
    }

    // Standard-Dropdown (mit optionaler Custom-Value-Option)
    return (
      <Box sx={{ minWidth: 120 }}>
        <FormControl fullWidth size="small" error={!!error}>
          <Select
            value={localValue || ""}
            onChange={(e) => {
              const newValue = e.target.value;
              if (newValue === CUSTOM_VALUE_MARKER) {
                // "Individuell eingeben..." gewählt -> Custom-Mode aktivieren
                setIsCustomMode(true);
                setLocalValue("");
              } else {
                handleChange(newValue);
              }
            }}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            sx={compactSelectStyles}
          >
            {fieldConfig.options?.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
            {fieldConfig.allowCustomValue && (
              <MenuItem
                key={CUSTOM_VALUE_MARKER}
                value={CUSTOM_VALUE_MARKER}
                sx={{
                  fontStyle: "italic",
                  borderTop: `1px solid ${colors.border.divider}`,
                  mt: 0.5,
                }}
              >
                {fieldConfig.customValueLabel || "Individuell eingeben..."}
              </MenuItem>
            )}
          </Select>
        </FormControl>
        {error && (
          <Typography
            variant="caption"
            color="error"
            sx={{ mt: 0.5, display: "block" }}
          >
            {error}
          </Typography>
        )}
      </Box>
    );
  }

  // Zahlen-Feld
  if (fieldConfig.type === "number") {
    return (
      <Box sx={{ minWidth: 100 }}>
        <TextField
          fullWidth
          size="small"
          value={localValue}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          error={!!error}
          inputRef={inputRef}
          slotProps={{
            input: {
              inputComponent: NumericFormatCustom as any,
              ...(fieldConfig.suffix && {
                endAdornment: (
                  <InputAdornment
                    position="end"
                    sx={{ "& .MuiTypography-root": { fontSize: "0.875rem" } }}
                  >
                    {fieldConfig.suffix}
                  </InputAdornment>
                ),
              }),
            },
            htmlInput: {
              allowDecimals: fieldConfig.allowDecimals ?? true,
              allowNegative: fieldConfig.allowNegative ?? false,
            },
          }}
          sx={compactInputStyles}
        />
        {error && (
          <Typography
            variant="caption"
            color="error"
            sx={{ mt: 0.5, display: "block" }}
          >
            {error}
          </Typography>
        )}
      </Box>
    );
  }

  // Autocomplete-Feld (freeSolo)
  if (fieldConfig.type === "autocomplete") {
    return (
      <Box sx={{ minWidth: 150 }}>
        <Autocomplete
          freeSolo
          options={fieldConfig.suggestions ?? []}
          value={localValue}
          onInputChange={(_, newValue) => {
            setLocalValue(newValue);
            onChange(newValue);
          }}
          onBlur={handleBlur}
          size="small"
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              error={!!error}
              inputRef={inputRef}
              onKeyDown={handleKeyDown}
              sx={compactInputStyles}
            />
          )}
        />
        {error && (
          <Typography
            variant="caption"
            color="error"
            sx={{ mt: 0.5, display: "block" }}
          >
            {error}
          </Typography>
        )}
      </Box>
    );
  }

  // Text-Feld (Standard)
  return (
    <Box sx={{ minWidth: 100 }}>
      <TextField
        fullWidth
        size="small"
        value={localValue}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        error={!!error}
        inputRef={inputRef}
        sx={compactInputStyles}
      />
      {error && (
        <Typography
          variant="caption"
          color="error"
          sx={{ mt: 0.5, display: "block" }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default EditableCell;
